import { useState, useEffect, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { label: "首頁", path: "/" },
  { label: "品牌故事", path: "/brand-story" },
  { label: "產品介紹", path: "/products" },
  { label: "最新消息", path: "/news" },
  { label: "職人日誌", path: "/craft-diary" },
  { label: "電商/禮盒", path: "/gift-box" },
  { label: "加盟專區", path: "/franchise" },
  { label: "聯絡我們", path: "/contact" },
];

const LOGO_URL =
  "https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/eff900376d0ae7cc09521c15ed9fd91b.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isActive = (path: string) => pathname === path;

  return (
    <Fragment>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
          scrolled || mobileOpen
            ? "bg-redbean-cream/95 backdrop-blur-md border-redbean-caramel/20 shadow-soft"
            : "bg-redbean-cream/90 backdrop-blur-sm border-transparent"
        }`}
      >
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{ maxWidth: "1200px" }}
        >
          <nav className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="來加綉綉紅豆餅品牌標誌"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-redbean-coffee font-heading font-bold text-lg md:text-xl whitespace-nowrap">
                來加綉綉紅豆餅
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-3 py-2 text-[1.0625rem] font-medium whitespace-nowrap transition-colors ${
                    isActive(item.path)
                      ? "text-redbean-primary"
                      : "text-redbean-coffee hover:text-redbean-primary"
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-redbean-primary rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <a
              href="https://lin.ee/xiNNjjf"
              rel="nofollow"
              target="_blank"
              className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-redbean-primary text-redbean-cream text-[1.0625rem] font-semibold rounded-full hover:bg-redbean-coffee transition-colors whitespace-nowrap min-h-[44px] cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-sm">
                <i className="ri-chat-3-line" />
              </span>
              加入 LINE 詢問
            </a>

            {/* Mobile Hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-redbean-primary cursor-pointer min-h-[44px] min-w-[44px]"
              aria-label={mobileOpen ? "關閉選單" : "開啟選單"}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i
                  className={
                    mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"
                  }
                />
              </div>
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-redbean-coffee/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bg-redbean-cream shadow-card max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4">
              {navItems.map((item, idx) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block py-3.5 text-[1.125rem] font-medium border-b ${
                    isActive(item.path)
                      ? "text-redbean-primary"
                      : "text-redbean-coffee"
                  } ${
                    idx === navItems.length - 1
                      ? "border-transparent"
                      : "border-redbean-caramel/15"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="https://lin.ee/xiNNjjf"
                rel="nofollow"
                target="_blank"
                className="mt-4 flex items-center justify-center gap-2 px-5 py-3 bg-redbean-primary text-redbean-cream text-[1.0625rem] font-semibold rounded-full whitespace-nowrap min-h-[44px] cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-sm">
                  <i className="ri-chat-3-line" />
                </span>
                加入 LINE 詢問
              </a>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}